import {
  normalizeKey,
  resolveCanonicalListValue,
} from '../../shared/verteoffice/exports/helpers/helpers'
import { buildDependencyConfiguration, type DomainDataList } from './dependencyConfig'
import type { DomainNode } from './buildDomainNodes'
import { LIST_VALIDATION_CONFIGS } from './validationListConfigs'
import { applyActivityCascadeToRecord } from './activityCascade'

const TARGET_FIELDS = ['general_activity', 'specific_activity', 'activity_objective'] as const

type TargetField = (typeof TARGET_FIELDS)[number]

function getAllowedValuesForField (
  field: TargetField,
  record: Record<string, any>,
  domainDataList: DomainDataList,
  dependencyConfig: ReturnType<typeof buildDependencyConfiguration>
): string[] {
  const config = LIST_VALIDATION_CONFIGS.find(item => item.columnKey === field)
  if (!config) return []

  const dependencyEntry = Object.entries(dependencyConfig).find(([, dep]) =>
    dep.children.includes(field)
  )

  if (dependencyEntry) {
    const [parentField, dependency] = dependencyEntry
    const parentValue = record[parentField]
    const normalizedParentValue = normalizeKey(String(parentValue ?? ''))
    const matchingKey = Object.keys(dependency.options)
      .find(key => key !== 'default' && normalizeKey(key) === normalizedParentValue)

    const constrainedOptions = dependency.options[matchingKey ?? 'default']?.[field]
    if (Array.isArray(constrainedOptions)) {
      return constrainedOptions
    }
  }

  if (config.listKey) return domainDataList[config.listKey] ?? []
  if (config.sources?.length) return config.sources.flatMap(key => domainDataList[key] ?? [])
  return []
}

export function canonicalizeWorklogListFields<T extends Record<string, any>> (
  record: T,
  domainDataList: DomainDataList | undefined,
  nodes?: DomainNode[]
): T {
  if (!domainDataList) return record

  const nextRecord = applyActivityCascadeToRecord(record, domainDataList, nodes)
  const dependencyConfig = buildDependencyConfiguration(domainDataList, nodes)

  TARGET_FIELDS.forEach((field) => {
    const rawValue = nextRecord[field]
    if (rawValue == null || String(rawValue).trim() === '') return

    const allowedValues = getAllowedValuesForField(field, nextRecord, domainDataList, dependencyConfig)
    const canonicalValue = resolveCanonicalListValue(rawValue, allowedValues, 'normalized')
    if (canonicalValue) {
      nextRecord[field] = canonicalValue
    }
  })

  return nextRecord
}
